<template>
  <transition name="osc-fade">
    <div v-if="guardVisible && displayedPlanes.length" class="osc-guard">
      <div class="osc-guard__panel">
        <div class="osc-guard__header">
          <i class="el-icon-warning-outline"></i>
          <div>
            <div class="osc-guard__title">{{ $t("飞机摆动风险") }}</div>
            <div class="osc-guard__desc">{{ $t(safetyText) }}</div>
          </div>
        </div>

        <UiTabs
          v-model="activePlaneKey"
          class="osc-guard__tabs"
          type="card"
          :class="{ 'is-single': displayedPlanes.length <= 1 }">
          <UiTabPane
            v-for="plane in displayedPlanes"
            :key="getPlaneKey(plane)"
            :name="getPlaneKey(plane)"
            :label="plane.name || plane.plane_code || '未知飞机'"
            lazy>
            <div class="osc-guard__item">
              <UiIconControl class="osc-guard__close" size="small" :title="$t('关闭')" icon="el-icon-close" @click="dismissPlane(plane)" />

              <div class="osc-guard__info">
                <div class="osc-guard__name">{{ plane.name || plane.plane_code || '未知飞机' }}</div>
                <div class="osc-guard__meta">
                  <span>{{ $t(plane.plane_code || '无编号') }}</span>
                  <span>{{ $t(plane.planeState && plane.planeState.online ? '在线' : '离线') }}</span>
                </div>
                <transition name="osc-fuse-deal">
                  <div v-if="fuseSliderPlaneKey === getPlaneKey(plane)" class="osc-guard__fuse-panel">
                    <div class="osc-guard__fuse-title">
                      <span>{{ $t("向右滑动确认熔断") }}</span>
                      <UiIconControl class="osc-guard__fuse-close" size="small" variant="ghost" :title="$t('取消熔断')" icon="el-icon-close" @click="closeFuseSlider" />
                    </div>
                    <GroundStationSlideConfirm
                      idle-text="向右滑动"
                      ready-text="松开执行熔断"
                      icon-class="iconfont icon-baoxiansirongduan"
                      danger
                      @confirm="executeFuse(plane)"
                    />
                  </div>
                </transition>
                <div class="osc-guard__actions">
                  <UiButton
                    class="osc-guard__button"
                    variant="control"
                    :disabled="!plane.plane_code"
                    @click="clearOscillation(plane)">
                    <i class="iconfont icon-zhendong"></i>
                    <span>{{ $t("执行消摆") }}</span>
                  </UiButton>
                  <UiButton
                    v-if="hasTransportFuse(plane)"
                    class="osc-guard__button danger"
                    variant="control"
                    tone="danger"
                    :disabled="!plane.plane_code"
                    @click="toggleFuseSlider(plane)">
                    <i class="iconfont icon-baoxiansirongduan"></i>
                    <span>{{ $t("挂载绳熔断") }}</span>
                  </UiButton>
                </div>
              </div>

              <div class="osc-guard__video">
                <VideoBox :plane="plane" fit="cover" />
              </div>
            </div>
          </UiTabPane>
        </UiTabs>
      </div>
    </div>
  </transition>
</template>

<script>
import { t } from '@/i18n'
import mqtt from '@/utils/mqtt'
import VideoBox from '@/components/VideoBox'
import GroundStationSlideConfirm from '@/components/ground-station/GroundStationSlideConfirm'
import { hasPlaneModule } from '@/utils/planeModules'
import { speakText } from '@/utils/index'

const SAFETY_TEXT = '检测到飞机大幅摇摆，当前状态很危险。请先确认飞机周围没有障碍物，再点击消摆。'

export default {
  name: 'OscillationGuard',
  components: {
    VideoBox,
    GroundStationSlideConfirm
  },
  data () {
    return {
      dismissedMap: {},
      activePlaneKey: '',
      guardVisible: false,
      displayedPlanes: [],
      fuseSliderPlaneKey: ''
    }
  },
  computed: {
    safetyText () {
      return SAFETY_TEXT
    },
    rawOscillationPlanes () {
      return (this.$store.state.airList || []).filter(plane => {
        const state = plane && plane.planeState ? plane.planeState : {}
        return state.oscStatus === true || state.oscStatus === 1 || state.oscStatus === '1'
      })
    },
    activePlanes () {
      return this.rawOscillationPlanes.filter(plane => {
        const state = plane && plane.planeState ? plane.planeState : {}
        return this.dismissedMap[plane.plane_code] !== state.oscStatusTick
      })
    }
  },
  watch: {
    activePlanes: {
      immediate: true,
      handler (planes) {
        if (!planes.length) {
          return
        }

        // 锁存已经触发语音的告警飞机，避免后续 MQTT 刷新在渲染前撤掉窗口。
        this.displayedPlanes = planes.slice()
        if (!this.guardVisible) {
          speakText(SAFETY_TEXT)
          this.guardVisible = true
        }

        if (!planes.some(plane => this.getPlaneKey(plane) === this.activePlaneKey)) {
          this.activePlaneKey = this.getPlaneKey(planes[0])
        }
      }
    },
    rawOscillationPlanes (planes) {
      const activeCodeSet = new Set(planes.map(plane => plane.plane_code).filter(Boolean))

      Object.keys(this.dismissedMap).forEach(planeCode => {
        if (!activeCodeSet.has(planeCode)) {
          this.$delete(this.dismissedMap, planeCode)
        }
      })
    },
    activePlaneKey () {
      this.closeFuseSlider()
    }
  },
  methods: {
    getPlaneKey (plane) {
      if (!plane) return ''
      return String(plane.plane_code || plane.id || plane.name || '')
    },
    dismissPlane (plane) {
      if (plane && plane.plane_code) {
        const state = plane.planeState || {}
        this.$set(this.dismissedMap, plane.plane_code, state.oscStatusTick || Date.now())
      }
      const key = this.getPlaneKey(plane)
      this.displayedPlanes = this.displayedPlanes.filter(item => this.getPlaneKey(item) !== key)
      if (!this.displayedPlanes.length) {
        this.activePlaneKey = ''
        this.guardVisible = false
      } else if (!this.displayedPlanes.some(item => this.getPlaneKey(item) === this.activePlaneKey)) {
        this.activePlaneKey = this.getPlaneKey(this.displayedPlanes[0])
      }
      this.closeFuseSlider()
    },
    hasTransportFuse (plane) {
      return hasPlaneModule(plane, 'transport_fuse')
    },
    toggleFuseSlider (plane) {
      const key = this.getPlaneKey(plane)
      this.fuseSliderPlaneKey = this.fuseSliderPlaneKey === key ? '' : key
    },
    closeFuseSlider () {
      this.fuseSliderPlaneKey = ''
    },
    executeFuse (plane) {
      if (!plane || !plane.plane_code) {
        this.$message.warning(t('飞机通信信息缺失'))
        return
      }
      const ok = mqtt.publishCommand(plane, '{"fuseCutoff":1}')
      if (!ok) {
        this.$message.error(t('指令服务器未连接，熔断指令发送失败'))
        return
      }
      speakText('执行挂载绳熔断')
      this.$store.dispatch('fetchLog', {
        sourceType: 'terminal',
        planeId: plane.id,
        planeName: plane.name || plane.plane_code,
        content: '执行挂载绳熔断'
      })
      this.dismissPlane(plane)
    },
    clearOscillation (plane) {
      if (!plane || !plane.plane_code) {
        this.$message.warning(t('飞机通信信息缺失'))
        return
      }

      const ok = mqtt.publishCommand(plane, '{"clearOscillation":1}')
      if (!ok) {
        this.$message.error(t('指令服务器未连接，消摆指令发送失败'))
        return
      }

      speakText('消摆')
      this.$store.dispatch('fetchLog', {
        sourceType: 'terminal',
        planeId: plane.id,
        planeName: plane.name || plane.plane_code,
        content: '执行消摆'
      })
      this.dismissPlane(plane)
    }
  }
}
</script>

<style lang="scss" scoped>
.osc-guard {
  position: fixed;
  inset: 0;
  z-index: 30000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ui-space-lg);
  background: rgba(var(--ui-color-black-rgb), .62);
  backdrop-filter: blur(5px);
  box-sizing: border-box;
}

.osc-guard__panel {
  width: min(980px, 100%);
  max-height: calc(100vh - (var(--ui-space-lg) * 2));
  overflow: hidden;
  padding: var(--ui-space-lg);
  border: 1px solid var(--ui-border-color-strong);
  border-radius: 8px;
  background: var(--ui-surface-finish, linear-gradient(transparent, transparent)), var(--ui-bg-surface);
  box-shadow: var(--ui-shadow-floating);
  box-sizing: border-box;
}

.osc-guard__header {
  display: flex;
  gap: var(--ui-space-sm);
  align-items: flex-start;
  padding: 0 0 var(--ui-space-md);
  margin-bottom: var(--ui-space-md);
  color: var(--ui-color-danger);
}

.osc-guard__header i {
  font-size: 34px;
  line-height: 1;
}

.osc-guard__title {
  color: var(--ui-text-primary);
  font-size: 18px;
  line-height: 1.2;
  font-weight: 700;
}

.osc-guard__desc {
  margin-top: 5px;
  color: var(--ui-text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.osc-guard__tabs {
  width: 100%;
}

.osc-guard__tabs.is-single ::v-deep .el-tabs__header {
  display: none;
}

.osc-guard__tabs ::v-deep .el-tabs__item {
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
}

.osc-guard__item {
  position: relative;
  display: grid;
  grid-template-columns: minmax(210px, 260px) minmax(320px, 560px);
  justify-content: center;
  gap: var(--ui-space-md);
  padding: var(--ui-space-md) calc(var(--ui-space-lg) * 2) var(--ui-space-md) var(--ui-space-md);
  border: 1px solid var(--ui-border-color);
  border-radius: 7px;
  background: rgba(var(--ui-bg-control-rgb), .9);
}

.osc-guard__close {
  --terminal-control-color: var(--ui-text-muted);
  --terminal-control-hover-color: var(--ui-color-danger-emphasis);
  --terminal-control-feedback-color: var(--ui-color-danger-emphasis);
  --terminal-control-feedback-rgb: var(--ui-color-danger-emphasis-rgb);
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.osc-guard__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.osc-guard__name {
  font-size: 17px;
  font-weight: 700;
  color: var(--ui-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.osc-guard__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: var(--ui-space-sm);
  color: var(--ui-text-muted);
  font-size: 12px;
}

.osc-guard__actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--ui-space-sm);
  margin-top: auto;
}

.osc-guard__actions > .osc-guard__button:only-child {
  grid-column: 1 / -1;
}

.osc-guard__button {
  width: 100%;
  min-height: 62px;
}

.osc-guard__fuse-panel {
  margin-top: auto;
  padding: var(--ui-space-sm);
  border: 1px solid rgba(var(--ui-color-danger-emphasis-rgb), .45);
  border-radius: 6px;
  background: var(--ui-bg-panel-elevated);
  transform-origin: top left;
}

.osc-guard__fuse-panel + .osc-guard__actions {
  margin-top: var(--ui-space-sm);
}

.osc-guard__fuse-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--ui-space-xs);
  color: var(--ui-text-primary);
  font-size: var(--gs-font-caption, 11px);
}

.osc-guard__fuse-close {
  color: var(--ui-text-secondary);
}

.osc-guard__video {
  width: 100%;
  aspect-ratio: 16 / 9;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--ui-border-color);
  border-radius: 6px;
  background: var(--ui-bg-control);
}

.osc-fuse-deal-enter-active { animation: osc-fuse-card-in .36s cubic-bezier(.2, .72, .24, 1) both; }
.osc-fuse-deal-leave-active { animation: osc-fuse-card-out .16s ease-in both; }

@keyframes osc-fuse-card-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes osc-fuse-card-out {
  to { opacity: 0; }
}

.osc-fade-enter-active,
.osc-fade-leave-active {
  transition: opacity 0.1s ease;
}

.osc-fade-enter,
.osc-fade-leave-to {
  opacity: 0;
}

@media (max-width: 720px) {
  .osc-guard {
    padding: var(--ui-space-sm);
  }

  .osc-guard__panel {
    padding: var(--ui-space-md);
    max-height: calc(100vh - (var(--ui-space-sm) * 2));
  }

  .osc-guard__tabs ::v-deep .el-tabs__item {
    max-width: 150px;
  }

  .osc-guard__item {
    grid-template-columns: 1fr;
    padding: calc(var(--ui-space-lg) * 2) var(--ui-space-sm) var(--ui-space-sm);
  }
}
</style>
